export enum NotificationType {
  // Order-related notifications
  ORDER_PLACED = "order_placed",
  ORDER_UPDATED = "order_updated",
  ORDER_READY = "order_ready",
  ORDER_DELIVERED = "order_delivered",
  ORDER_CANCELLED = "order_cancelled",
  ORDER_PAYMENT_RECEIVED = "order_payment_received",
  ORDER_PAYMENT_FAILED = "order_payment_failed",
  
  // Staff/User notifications
  STAFF_INVITATION_SENT = "staff_invitation_sent",
  STAFF_INVITATION_ACCEPTED = "staff_invitation_accepted",
  STAFF_INVITATION_DECLINED = "staff_invitation_declined",
  STAFF_ROLE_CHANGED = "staff_role_changed",
  STAFF_JOINED = "staff_joined",
  STAFF_LEFT = "staff_left",
  USER_REGISTERED = "user_registered",
  USER_LOGIN = "user_login",
  
  // Chat notifications
  NEW_MESSAGE = "new_message",
  MESSAGE_REPLY = "message_reply",
  CHAT_MENTION = "chat_mention",
  
  // Inventory notifications
  INVENTORY_LOW_STOCK = "inventory_low_stock",
  INVENTORY_OUT_OF_STOCK = "inventory_out_of_stock",
  INVENTORY_EXPIRING = "inventory_expiring",
  INVENTORY_EXPIRED = "inventory_expired",
  INVENTORY_ITEM_ADDED = "inventory_item_added",
  INVENTORY_ITEM_UPDATED = "inventory_item_updated",
  INVENTORY_ITEM_DELETED = "inventory_item_deleted",
  
  // Reservation notifications
  RESERVATION_CREATED = "reservation_created",
  RESERVATION_CONFIRMED = "reservation_confirmed",
  RESERVATION_CANCELLED = "reservation_cancelled",
  RESERVATION_MODIFIED = "reservation_modified",
  RESERVATION_REMINDER = "reservation_reminder",
  
  // Payment notifications
  PAYMENT_SUCCESSFUL = "payment_successful",
  PAYMENT_FAILED = "payment_failed",
  PAYMENT_REFUNDED = "payment_refunded",
  SUBSCRIPTION_RENEWED = "subscription_renewed",
  SUBSCRIPTION_EXPIRED = "subscription_expired",
  SUBSCRIPTION_CANCELLED = "subscription_cancelled",
  
  // System notifications
  SYSTEM_UPDATE = "system_update",
  SYSTEM_MAINTENANCE = "system_maintenance",
  SYSTEM_ERROR = "system_error",
  FEATURE_ANNOUNCEMENT = "feature_announcement",
  SECURITY_ALERT = "security_alert",
  
  // Customer service notifications
  CUSTOMER_SUPPORT_REQUEST = "customer_support_request",
  CUSTOMER_FEEDBACK = "customer_feedback",
  CUSTOMER_COMPLAINT = "customer_complaint",
  
  // Table/Service notifications
  WAITER_REQUESTED = "waiter_requested",
  WAITER_REQUEST = "waiter_request",
  TABLE_READY = "table_ready",
  TABLE_ASSIGNED = "table_assigned",
  TABLE_CLEARED = "table_cleared",
  
  // Contact/Inquiry notifications
  CONTACT_FORM_SUBMITTED = "contact_form_submitted",
  DEMO_REQUEST = "demo_request",
  PARTNERSHIP_INQUIRY = "partnership_inquiry",
  
  // Marketing notifications
  NEWSLETTER_SENT = "newsletter_sent",
  PROMOTION_ACTIVE = "promotion_active",
  LOYALTY_REWARD = "loyalty_reward",
  
  // Analytics notifications
  SALES_TARGET_MET = "sales_target_met",
  SALES_TARGET_MISSED = "sales_target_missed",
  PEAK_HOURS_ALERT = "peak_hours_alert",
  SLOW_PERIOD_ALERT = "slow_period_alert"
}

export enum NotificationEntityType {
  ORDER = "order",
  USER = "user",
  STAFF = "staff",
  INVENTORY = "inventory",
  RESERVATION = "reservation",
  PAYMENT = "payment",
  TABLE = "table",
  CHAT = "chat",
  SYSTEM = "system",
  CONTACT = "contact",
  ANALYTICS = "analytics"
}

export enum NotificationPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  URGENT = "urgent"
}

export enum NotificationStatus {
  UNREAD = "unread",
  READ = "read",
  ARCHIVED = "archived"
}

export interface SystemNotification {
  id: string;
  store_id: string;
  user_id: string;
  type: NotificationType;
  entity_type?: NotificationEntityType;
  entity_id?: string;
  title: string;
  message: string;
  status: NotificationStatus;
  priority: NotificationPriority;
  created_at: string;
  updated_at: string;
  read_at?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationTemplate {
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  entity_type?: NotificationEntityType;
}

export const getNotificationCategory = (type: NotificationType): string => {
  if (type.includes('order')) return 'orders';
  if (type.includes('staff') || type.includes('user')) return 'staff';
  if (type.includes('chat') || type.includes('message')) return 'chat';
  if (type.includes('inventory')) return 'inventory';
  if (type.includes('reservation')) return 'reservations';
  if (type.includes('payment') || type.includes('subscription')) return 'payments';
  if (type.includes('system') || type.includes('update') || type.includes('maintenance')) return 'system';
  if (type.includes('contact') || type.includes('inquiry') || type.includes('demo')) return 'contact';
  if (type.includes('waiter') || type.includes('table')) return 'service';
  if (type.includes('marketing') || type.includes('newsletter') || type.includes('promotion')) return 'marketing';
  if (type.includes('analytics') || type.includes('sales') || type.includes('target')) return 'analytics';
  return 'other';
};

export const getNotificationIcon = (type: NotificationType): string => {
  const category = getNotificationCategory(type);
  switch (category) {
    case 'orders': return 'shopping-bag';
    case 'staff': return 'users';
    case 'chat': return 'message-square';
    case 'inventory': return 'package';
    case 'reservations': return 'calendar';
    case 'payments': return 'credit-card';
    case 'system': return 'settings';
    case 'contact': return 'mail';
    case 'service': return 'bell';
    case 'marketing': return 'megaphone';
    case 'analytics': return 'trending-up';
    default: return 'bell';
  }
};

export const getNotificationColor = (type: NotificationType, status: NotificationStatus): string => {
  if (status === NotificationStatus.UNREAD) {
    return 'bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-l-blue-500 hover:from-blue-100 hover:to-indigo-100';
  }
  
  const category = getNotificationCategory(type);
  switch (category) {
    case 'orders': return 'bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-l-orange-500 hover:from-orange-100 hover:to-red-100';
    case 'payments': return 'bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-l-emerald-500 hover:from-emerald-100 hover:to-green-100';
    case 'staff': return 'bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-l-blue-500 hover:from-blue-100 hover:to-cyan-100';
    case 'inventory': return 'bg-gradient-to-r from-purple-50 to-violet-50 border-l-4 border-l-purple-500 hover:from-purple-100 hover:to-violet-100';
    case 'reservations': return 'bg-gradient-to-r from-indigo-50 to-blue-50 border-l-4 border-l-indigo-500 hover:from-indigo-100 hover:to-blue-100';
    case 'chat': return 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-l-green-500 hover:from-green-100 hover:to-emerald-100';
    case 'system': return 'bg-gradient-to-r from-gray-50 to-slate-50 border-l-4 border-l-gray-500 hover:from-gray-100 hover:to-slate-100';
    case 'contact': return 'bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-l-cyan-500 hover:from-cyan-100 hover:to-blue-100';
    case 'service': return 'bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-l-yellow-500 hover:from-yellow-100 hover:to-orange-100';
    case 'marketing': return 'bg-gradient-to-r from-pink-50 to-rose-50 border-l-4 border-l-pink-500 hover:from-pink-100 hover:to-rose-100';
    case 'analytics': return 'bg-gradient-to-r from-teal-50 to-cyan-50 border-l-4 border-l-teal-500 hover:from-teal-100 hover:to-cyan-100';
    default: return 'bg-gradient-to-r from-gray-50 to-slate-50 border-l-4 border-l-gray-500 hover:from-gray-100 hover:to-slate-100';
  }
}; 